// useTableNodelistHydration.js

import { useCallback, useMemo } from 'react';

import useNodesByKeysQuery from 'hooks/useNodesByKeysQuery';
import {
  getNodelistNodeTitle,
  normalizeNodelistKeys,
} from 'utilities/NodelistField';
import { isRelationFieldType } from 'utilities/FieldTypes';
import { resolveFieldValue } from 'views/table/utils/tableFieldUtils';

/**
 * Batches related-node title hydration for visible Relation columns.
 *
 * @param {object} params
 * @param {object[]|null|undefined} params.nodes - Rows that may reference related nodes.
 * @param {Map|null|undefined} params.schemaFieldMap - Field definitions by key.
 * @param {string[]|null|undefined} params.fieldKeys - Visible field keys to inspect.
 * @param {string|null|undefined} params.fieldDataSection - Node data section for typed fields.
 *
 * @returns {{ resolveNodelistTitle: (nodeKey: string) => string }}
 */
export default function useTableNodelistHydration({
  nodes,
  schemaFieldMap,
  fieldKeys,
  fieldDataSection,
} = {}) {
  const relatedKeys = useMemo(() => {
    const nodelistFields = (Array.isArray(fieldKeys) ? fieldKeys : [])
      .map((fieldKey) => schemaFieldMap?.get?.(fieldKey))
      .filter((fieldDef) => isRelationFieldType(fieldDef));

    if (!nodelistFields.length) return [];

    const keys = new Set();
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      nodelistFields.forEach((fieldDef) => {
        normalizeNodelistKeys(
          resolveFieldValue(node, fieldDef, fieldDataSection),
        ).forEach((key) => keys.add(key));
      });
    });

    return [...keys];
  }, [fieldDataSection, fieldKeys, nodes, schemaFieldMap]);

  const { nodesByKey, isLoading } = useNodesByKeysQuery(relatedKeys, {
    enabled: relatedKeys.length > 0,
  });

  const resolveNodelistTitle = useCallback((nodeKey) => {
    const key = String(nodeKey || '').trim();
    if (!key) return '';

    const related = nodesByKey?.get?.(key);
    if (related) return getNodelistNodeTitle(related);

    // Avoid flashing raw Firestore keys while the batch fetch is in flight.
    if (isLoading) return '';

    return 'Unavailable';
  }, [isLoading, nodesByKey]);

  return { resolveNodelistTitle };
}
